import Axios, { API } from './api';
import { getGlobalState, setGlobalState, useGlobalState } from 'src/state/globalstate';
import { isDemoUser } from './roles';

// Backend-driven feature limits for the Demo role (see API.roleLimits). The
// config governs UX only — the backend must also clamp server-side. A missing
// limit means "unlimited", so partial configs are safe.

export type FeatureLimits = {
  maxDocuments?: number;
  maxPagesPerDocument?: number;
  maxLines?: number;
  maxResults?: number;
  maxRegionsPerAnnotation?: number;
  maxAnnotations?: number;
  allowAnnotationDelete?: boolean;
  allowDelete?: boolean;
};

export type FeatureConfig = {
  key: string;
  label?: string;
  enabled?: boolean;
  limits?: FeatureLimits;
};

export type RoleLimitsConfig = {
  role?: string;
  roleId?: number;
  features?: FeatureConfig[];
};

// Stable feature keys — the contract with the backend config `key` field.
export type FeatureKey =
  | 'documents'
  | 'pasteText'
  | 'usLetterSearch'
  | 'irs'
  | 'claimReview'
  | 'documentAnnotator';

// Fetch the Demo limits once and cache in global state. Safe no-op for
// non-demo users. userId/token are auto-injected by the Axios interceptor.
export const fetchRoleLimits = async () => {
  if (!isDemoUser(getGlobalState('userRoles') as any)) return;
  if (getGlobalState('roleLimits')) return; // already loaded
  try {
    const res = await Axios.get(API.roleLimits, { params: { role: 'Demo' } });
    setGlobalState('roleLimits', res.data || null);
  } catch (e) {
    // Leave null → no limits applied (fails open to full access).
  }
};

const pickFeature = (
  config: RoleLimitsConfig | null,
  key: FeatureKey
): FeatureConfig | undefined =>
  (config?.features || []).find((f) => f.key === key);

// Non-hook read for use inside handlers/callbacks. Returns the feature's limits
// object, or null when the current user isn't a demo user / config not loaded /
// feature not present (all of which mean "no restriction").
export const getFeatureLimits = (key: FeatureKey): FeatureLimits | null => {
  if (!isDemoUser(getGlobalState('userRoles') as any)) return null;
  const config = getGlobalState('roleLimits') as RoleLimitsConfig | null;
  return pickFeature(config, key)?.limits ?? null;
};

// Reactive variant for render-time gating (e.g. hiding a delete button).
export const useFeatureLimits = (key: FeatureKey): FeatureLimits | null => {
  const [userRoles] = useGlobalState('userRoles');
  const [roleLimits] = useGlobalState('roleLimits');
  if (!isDemoUser(userRoles as any)) return null;
  return pickFeature(roleLimits as RoleLimitsConfig | null, key)?.limits ?? null;
};
